import { computed, onBeforeUnmount, ref, watch } from "vue";
import { onBeforeRouteLeave } from "vue-router";

const cloneValue = (value) => JSON.parse(JSON.stringify(value ?? {}));
const serializeValue = (value) => JSON.stringify(value ?? {});

export default function useManualFormState(form, options = {}) {
  const editable = ref(false);
  const saving = ref(false);
  const saveError = ref("");
  const lastSavedAt = ref(null);
  const baselineValue = ref(cloneValue({}));
  const baselineSignature = ref(serializeValue({}));
  const leaveMessage =
    options.leaveMessage ?? "You have unsaved changes. Leave this page?";

  const isDirty = computed(
    () => editable.value && serializeValue(form.value) !== baselineSignature.value,
  );
  const canSave = computed(() => editable.value && isDirty.value && !saving.value);

  const sync = (nextValue) => {
    const cloned = cloneValue(nextValue);
    form.value = cloned;
    baselineValue.value = cloneValue(cloned);
    baselineSignature.value = serializeValue(cloned);
    saveError.value = "";
  };

  const startEditing = () => {
    editable.value = true;
    saveError.value = "";
  };

  const cancelEditing = () => {
    form.value = cloneValue(baselineValue.value);
    editable.value = false;
    saving.value = false;
    saveError.value = "";
  };

  const startSaving = () => {
    saving.value = true;
    saveError.value = "";
  };

  const markSaved = (nextValue = form.value) => {
    const cloned = cloneValue(nextValue);
    form.value = cloned;
    baselineValue.value = cloneValue(cloned);
    baselineSignature.value = serializeValue(cloned);
    editable.value = false;
    saving.value = false;
    saveError.value = "";
    lastSavedAt.value = new Date();
  };

  const markSaveFailed = (message = "Unable to save changes.") => {
    saving.value = false;
    saveError.value = message;
  };

  const statusText = computed(() => {
    if (saving.value) return "Saving...";
    if (saveError.value) return saveError.value;
    if (editable.value && isDirty.value) return "Unsaved changes";
    if (editable.value) return "No changes";
    if (lastSavedAt.value) {
      return `Saved ${lastSavedAt.value.toLocaleTimeString()}`;
    }
    return "View mode";
  });

  const statusTone = computed(() => {
    if (saving.value) return "saving";
    if (saveError.value) return "error";
    if (editable.value && isDirty.value) return "dirty";
    return "idle";
  });

  const shouldWarnBeforeLeave = computed(
    () => editable.value && isDirty.value && !saving.value,
  );

  const handleBeforeUnload = (event) => {
    if (!shouldWarnBeforeLeave.value) {
      return undefined;
    }

    event.preventDefault();
    event.returnValue = leaveMessage;
    return leaveMessage;
  };

  watch(
    shouldWarnBeforeLeave,
    (shouldWarn) => {
      if (typeof window === "undefined") {
        return;
      }

      if (shouldWarn) {
        window.addEventListener("beforeunload", handleBeforeUnload);
        return;
      }

      window.removeEventListener("beforeunload", handleBeforeUnload);
    },
    { immediate: true },
  );

  onBeforeRouteLeave(() => {
    if (!shouldWarnBeforeLeave.value || typeof window === "undefined") {
      return true;
    }

    return window.confirm(leaveMessage);
  });

  onBeforeUnmount(() => {
    if (typeof window !== "undefined") {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    }
  });

  return {
    editable,
    saving,
    saveError,
    lastSavedAt,
    isDirty,
    canSave,
    statusText,
    statusTone,
    sync,
    startEditing,
    cancelEditing,
    startSaving,
    markSaved,
    markSaveFailed,
  };
}
